import { useCallback } from 'react';
import { Task } from '../../types';
import { getLocalDateKey } from './dateUtils';
import { getFlexiblePrecision, getTaskStartDate } from './scheduleUtils';
import { PlannerTaskInfo, TimelineScale } from './types';
import { PlannerRefs } from './usePlannerRefs';
import { PlannerState } from './usePlannerState';

export const usePlannerNavigation = (
    state: PlannerState, refs: PlannerRefs, tasks: PlannerTaskInfo[]
) => {
    const {
        selectedDate, setActiveTimelineDateKey, setActiveTimelineLabel, setSelectedDate,
        setShowSchedule, timelineScale, setTimelineScale, setTimelineWindow,
    } = state;
    const {
        isProgrammaticTimelineNavigationRef, pendingTimelineCurrentScrollRef,
        pendingTimelineCurrentSmoothScrollRef, pendingTimelineDaysViewDateRef,
        pendingTimelineTaskIdScrollRef, pendingTimelineTaskScrollAttemptsRef,
        pendingTimelineTaskScrollRef, pendingTimelineZoomTargetRef,
        suppressNextTimelineCurrentScrollRef, timelineRef,
    } = refs;
    const focusTask = useCallback((task: Task, closePanels: boolean) => {
        const date = getTaskStartDate(task);
        if (!date) return;
        const precision = getFlexiblePrecision(task);
        const targetScale = precision === 'year' ? 'year' : precision === 'month' ? 'month' : 'day';
        pendingTimelineTaskScrollRef.current = date;
        pendingTimelineTaskIdScrollRef.current = task.id;
        pendingTimelineTaskScrollAttemptsRef.current = 0;
        pendingTimelineCurrentScrollRef.current = false;
        isProgrammaticTimelineNavigationRef.current = true;
        suppressNextTimelineCurrentScrollRef.current = true;
        setActiveTimelineDateKey(getLocalDateKey(date));
        setActiveTimelineLabel('');
        setSelectedDate(date);
        if (closePanels) {
            setShowSchedule(false);
        }
        setTimelineScale(targetScale);
        setTimelineWindow({ start: -1, end: 1 });
    }, [isProgrammaticTimelineNavigationRef, pendingTimelineCurrentScrollRef,
        pendingTimelineTaskIdScrollRef, pendingTimelineTaskScrollAttemptsRef,
        pendingTimelineTaskScrollRef, setActiveTimelineDateKey, setActiveTimelineLabel,
        setSelectedDate, setShowSchedule, setTimelineScale, setTimelineWindow,
        suppressNextTimelineCurrentScrollRef]);
    const handleGoToNow = () => {
        const now = new Date();
        setActiveTimelineDateKey(getLocalDateKey(now));
        setActiveTimelineLabel('');
        setSelectedDate(now);
        pendingTimelineCurrentScrollRef.current = true;
        pendingTimelineCurrentSmoothScrollRef.current = true;
        setTimelineWindow(timelineScale === 'month' ? { start: 0, end: 0 } : { start: -1, end: 1 });
    };
    const handleGoToNextTask = useCallback(() => {
        const container = timelineRef.current;
        const viewport = container?.getBoundingClientRect();
        const visibleIds = new Set(Array.from(container?.querySelectorAll('[data-timeline-task-id]') || [])
            .filter((element) => {
                const rect = (element as HTMLElement).getBoundingClientRect();
                return viewport && rect.bottom > viewport.top && rect.top < viewport.bottom;
            }).map((element) => (element as HTMLElement).dataset.timelineTaskId).filter(Boolean));
        const visibleTimes = tasks.filter(({ task }) => visibleIds.has(task.id))
            .map(({ task }) => getTaskStartDate(task)?.getTime())
            .filter((time): time is number => time !== undefined);
        const after = visibleTimes.length ? Math.max(...visibleTimes) : selectedDate.getTime();
        const visibleInScale = tasks.filter(({ task }) => {
            const precision = getFlexiblePrecision(task);
            if (!getTaskStartDate(task) || task.completed) return false;
            if (timelineScale === 'day') return precision !== 'month' && precision !== 'year';
            if (timelineScale === 'month') return precision !== 'month' && precision !== 'year';
            if (timelineScale === 'year') return precision !== 'year';
            return true;
        }).sort((a, b) => getTaskStartDate(a.task)!.getTime() - getTaskStartDate(b.task)!.getTime());
        const next = visibleInScale.find(({ task }) => (
            !visibleIds.has(task.id) && getTaskStartDate(task)!.getTime() > after
        )) || visibleInScale.find(({ task }) => !visibleIds.has(task.id));
        if (!next) return;

        pendingTimelineTaskScrollRef.current = getTaskStartDate(next.task)!;
        pendingTimelineTaskIdScrollRef.current = next.task.id;
        pendingTimelineTaskScrollAttemptsRef.current = 0;
        pendingTimelineCurrentScrollRef.current = false;
        isProgrammaticTimelineNavigationRef.current = true;
        suppressNextTimelineCurrentScrollRef.current = true;
        const nextDate = getTaskStartDate(next.task)!;
        setActiveTimelineDateKey(getLocalDateKey(nextDate));
        setActiveTimelineLabel('');
        setSelectedDate(nextDate);
        setTimelineWindow(timelineScale === 'month' ? { start: 0, end: 0 } : { start: -1, end: 1 });
    }, [isProgrammaticTimelineNavigationRef, pendingTimelineCurrentScrollRef,
        pendingTimelineTaskIdScrollRef, pendingTimelineTaskScrollAttemptsRef,
        pendingTimelineTaskScrollRef, selectedDate, setActiveTimelineDateKey,
        setActiveTimelineLabel, setSelectedDate, setTimelineWindow,
        suppressNextTimelineCurrentScrollRef, tasks, timelineRef, timelineScale]);
    const handleOpenScheduledTask = (taskInfo: PlannerTaskInfo) => focusTask(taskInfo.task, true);
    const handleOpenDayInDaysView = useCallback((date: Date) => {
        pendingTimelineDaysViewDateRef.current = new Date(date);
        pendingTimelineCurrentScrollRef.current = false;
        suppressNextTimelineCurrentScrollRef.current = true;
        setActiveTimelineDateKey(getLocalDateKey(date));
        setActiveTimelineLabel('');
        setSelectedDate(new Date(date));
        setShowSchedule(false);
        setTimelineScale('month');
        setTimelineWindow({ start: 0, end: 0 });
    }, [pendingTimelineCurrentScrollRef, pendingTimelineDaysViewDateRef,
        setActiveTimelineDateKey, setActiveTimelineLabel, setSelectedDate,
        setShowSchedule, setTimelineScale, setTimelineWindow, suppressNextTimelineCurrentScrollRef]);
    const handleTimelineScaleChange = useCallback((scale: TimelineScale, anchorDate?: Date) => {
        const date = new Date(anchorDate || selectedDate);
        pendingTimelineZoomTargetRef.current = { date, scale };
        pendingTimelineCurrentScrollRef.current = false;
        suppressNextTimelineCurrentScrollRef.current = true;
        // The previous scale can be much taller than the target. Reset first so a
        // clamped scroll position cannot leave the newly selected period below view.
        timelineRef.current?.scrollTo({ top: 0 });
        setActiveTimelineDateKey(getLocalDateKey(date));
        setActiveTimelineLabel('');
        setSelectedDate(date);
        setTimelineScale(scale);
        setTimelineWindow(scale === 'month' ? { start: 0, end: 0 } : { start: -1, end: 1 });
    }, [pendingTimelineCurrentScrollRef, pendingTimelineZoomTargetRef, selectedDate,
        setActiveTimelineDateKey, setActiveTimelineLabel, setSelectedDate, setTimelineScale,
        setTimelineWindow, suppressNextTimelineCurrentScrollRef, timelineRef]);
    const handleZoomOutToParent = useCallback((scale: TimelineScale) => {
        handleTimelineScaleChange(scale, selectedDate);
    }, [handleTimelineScaleChange, selectedDate]);
    return {
        handleGoToNextTask, handleGoToNow, handleOpenDayInDaysView, handleOpenScheduledTask,
        handleTimelineScaleChange, handleZoomOutToParent,
    };
};
